import { useState } from 'react'
import { useAccountSettings, useUpdateAccountSettings } from '@/hooks/useAccountSettings'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { SettingsSaveError } from '@/lib/settingsSaveError'
import type { MaintenanceCategory } from '@/types'

// New accounts are created with this same list in their settings
// (Account::DEFAULT_MAINTENANCE_CATEGORIES in api/app/models/account.rb).
// It stays here as the fallback for an account created before that, which
// may hold none. Change both together.
export const DEFAULT_MAINTENANCE_CATEGORIES: MaintenanceCategory[] = [
  { key: 'plumbing', en: 'Plumbing', th: 'ประปา' },
  { key: 'electrical', en: 'Electrical', th: 'ไฟฟ้า' },
  { key: 'appliance', en: 'Appliance', th: 'เครื่องใช้ไฟฟ้า' },
  { key: 'structural', en: 'Structural', th: 'โครงสร้าง' },
  { key: 'pest_control', en: 'Pest Control', th: 'กำจัดสัตว์รบกวน' },
  { key: 'cleaning', en: 'Cleaning', th: 'ทำความสะอาด' },
  { key: 'other', en: 'Other', th: 'อื่นๆ' },
]

function normalizeCategories(cats: unknown): MaintenanceCategory[] {
  if (!Array.isArray(cats) || cats.length === 0) return DEFAULT_MAINTENANCE_CATEGORIES
  // Handle legacy string[] format migration
  if (typeof (cats as unknown[])[0] === 'string') {
    return (cats as string[]).map((key) => ({
      key,
      en: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      th: key,
    }))
  }
  return cats as MaintenanceCategory[]
}

export function useCategorySettings() {
  const { data: account } = useAccountSettings()
  const updateSettings = useUpdateAccountSettings()
  const translateError = useApiErrorTranslator('settings')
  // PAY-1 (T149-084): no client validation here (none invented), so this is
  // only ever set on a server failure. Cleared on the next save attempt.
  const [saveError, setSaveError] = useState<SettingsSaveError | null>(null)

  // null means "not yet edited by user" — use server data
  const [localCategories, setLocalCategories] = useState<MaintenanceCategory[] | null>(null)
  const [newCatEn, setNewCatEn] = useState('')
  const [newCatTh, setNewCatTh] = useState('')

  // Derive current categories: prefer local edits, fall back to server data
  const serverCategories = normalizeCategories(account?.settings.maintenance_categories)
  const categories = localCategories ?? serverCategories

  const setCategories = (updater: MaintenanceCategory[] | ((prev: MaintenanceCategory[]) => MaintenanceCategory[])) => {
    setLocalCategories((prev) => {
      const current = prev ?? serverCategories
      return typeof updater === 'function' ? updater(current) : updater
    })
  }

  const addCategory = () => {
    const enTrimmed = newCatEn.trim()
    const thTrimmed = newCatTh.trim()
    if (!enTrimmed) return
    const key = enTrimmed.toLowerCase().replace(/\s+/g, '_')
    if (categories.some((c) => c.key === key)) return
    setCategories((prev) => [...prev, { key, en: enTrimmed, th: thTrimmed || enTrimmed }])
    setNewCatEn('')
    setNewCatTh('')
  }

  const removeCategory = (index: number) => {
    setCategories((prev) => prev.filter((_, i) => i !== index))
  }

  const resetCategories = () => {
    setLocalCategories(DEFAULT_MAINTENANCE_CATEGORIES)
  }

  // PAY-1 (T149-084): returns true only when the save actually happened.
  // The caller shows success only on true.
  const handleSave = async (): Promise<boolean> => {
    setSaveError(null)
    try {
      await updateSettings.mutateAsync({
        settings: { maintenance_categories: categories },
      })
      return true
    } catch (err) {
      setSaveError({ serverMessage: translateError(err) })
      return false
    }
  }

  return {
    categories,
    newCatEn,
    newCatTh,
    setNewCatEn,
    setNewCatTh,
    isPending: updateSettings.isPending,
    saveError,
    addCategory,
    removeCategory,
    resetCategories,
    handleSave,
  }
}
